"use client";

import React, { useState, useEffect } from 'react';
import { gatewayClient } from '@/src/api';
import { useAuth } from '@/src/context/AuthContext';
import { getUserEmail } from '@/src/utils/getUserEmail';
import { resolveErrorMessage } from '@/src/utils/errorInterpolate';
import styles from './Contact.module.css';

interface ContactFormProps {
  translations: {
    title: string;
    placeholders: {
      name: string;
      email: string;
      subject: string;
      message: string;
    };
    submit: string;
    sending: string;
    successMessage: string;
    errorMessage: string;
    errors: Record<string, string>;
  };
}

const ContactForm: React.FC<ContactFormProps> = ({ translations }) => {
  const { user } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  useEffect(() => {
    const email = user?.email ?? getUserEmail();
    if (email) {
      setFormData(prev => ({ ...prev, email }));
    }
  }, [user?.email]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (error) setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      await gatewayClient.contact.submitContactForm(formData);

      setSubmitted(true);
      setFormData({ name: '', email: '', subject: '', message: '' });
      setTimeout(() => setSubmitted(false), 5000);
    } catch (err) {
      setError(resolveErrorMessage(err, translations.errors));
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={styles.formWrapper}>
      <div className={styles.formHeader}>
        <svg className={styles.formHeaderIcon} viewBox="0 0 24 24" fill="currentColor">
          <path d="M21 12.22C21 6.73 16.74 3 12 3c-4.69 0-9 3.65-9 9.28-.6.34-1 .98-1 1.72v2c0 1.1.9 2 2 2h1v-6.1c0-3.87 3.13-7 7-7s7 3.13 7 7V19h-8v2h8c1.1 0 2-.9 2-2v-1.22c.59-.31 1-.92 1-1.64v-2.3c0-.7-.41-1.31-1-1.62z" />
          <circle cx="9" cy="13" r="1" />
          <circle cx="15" cy="13" r="1" />
          <path d="M18 11.03A6.04 6.04 0 0 0 12.05 6c-3.03 0-6.29 2.51-6.03 6.45a8.075 8.075 0 0 0 4.86-5.89c1.31 2.63 4 4.44 7.12 4.47z" />
        </svg>
        <h2 className={styles.formTitle}>{translations.title}</h2>
      </div>

      {error && (
        <div className={`${styles.formAlert} ${styles.formAlertError}`}>{error}</div>
      )}

      {submitted && (
        <div className={`${styles.formAlert} ${styles.formAlertSuccess}`}>{translations.successMessage}</div>
      )}

      <form onSubmit={handleSubmit}>
        <div className={styles.formGrid}>
          <div className={styles.formGridHalf} style={{ display: 'grid', gap: 16 }}>
            <input
              className={styles.formInput}
              placeholder={translations.placeholders.name}
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              required
            />
            <input
              className={styles.formInput}
              placeholder={translations.placeholders.email}
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              required
            />
          </div>
          <input
            className={styles.formInput}
            placeholder={translations.placeholders.subject}
            name="subject"
            value={formData.subject}
            onChange={handleInputChange}
            required
          />
          <textarea
            className={`${styles.formInput} ${styles.formTextarea}`}
            placeholder={translations.placeholders.message}
            name="message"
            value={formData.message}
            onChange={handleInputChange}
            required
            rows={5}
          />
          <div>
            <button
              type="submit"
              className={styles.formSubmitButton}
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <span className={styles.formSpinner} />
                  {translations.sending}
                </>
              ) : (
                <>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
                  </svg>
                  {translations.submit}
                </>
              )}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default ContactForm;
